// authService.js
const BASE_URL = 'https://back-end-x7tx.onrender.com/api';

const AuthService = {
  fetchWithAuth: async (url, options = {}) => {
    const token = AuthService.getToken();

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });

    if (response.status === 401) {
      AuthService.logout();
      window.location.href = '/';
      return;
    }

    return response;
  },

  login: async (email, password) => {
    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Login failed');
      }

      localStorage.setItem('auth_token', data.token);
      if (data.user) {
        localStorage.setItem('user_data', JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await AuthService.fetchWithAuth(`${BASE_URL}/users/change-password`, {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to change password');
      }

      return data;
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
  },

  // ============================================
  // AGENT ONBOARDING (public — no JWT)
  //
  // The agent arrives from an emailed link and has never logged in, so these
  // deliberately use plain fetch, NOT fetchWithAuth: there's no token to send,
  // and fetchWithAuth's 401 handler would bounce them to "/" mid-flow.
  // The raw link token is the credential.
  // ============================================

  // Validate an onboarding link and find out where the agent is in the flow.
  getOnboardingSession: async (token) => {
    const response = await fetch(`${BASE_URL}/auth/onboarding/${token}`);
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'This onboarding link is not valid.');
    }

    return data.data;
  },

  // Onboarding step 1 — agent sets their own password.
  // On success the backend returns a real JWT, so we store the session exactly
  // like login() does and the agent lands in the portal already authenticated.
  setOnboardingPassword: async (token, password) => {
    const response = await fetch(`${BASE_URL}/auth/onboarding/${token}/password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to set password');
    }

    localStorage.setItem('auth_token', data.token);
    if (data.user) {
      localStorage.setItem('user_data', JSON.stringify(data.user));
    }

    return data;
  },

  getCurrentUser: () => {
    try {
      const userData = localStorage.getItem('user_data');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('auth_token');
  },

  getToken: () => {
    return localStorage.getItem('auth_token');
  },

  getUserRole: () => {
    const user = AuthService.getCurrentUser();
    return user ? user.role : null;
  },
};

export default AuthService;